/**
 * Geometry → label anchor.
 *
 * Anchors are computed in SCREEN pixels, not in degrees. A line's bearing and a
 * polygon's visual centre both depend on the projection, so working in the
 * projected space is what makes "along the line" actually look along the line.
 */

export interface Point2D {
  x: number;
  y: number;
}

export interface LabelAnchor extends Point2D {
  /** Radians. Non-zero only for line anchors. */
  angle: number;
}

/** Projects one [lon, lat] pair into container pixels. */
export type Project = (lon: number, lat: number) => Point2D;

function isPosition(value: unknown): value is [number, number] {
  return Array.isArray(value) && typeof value[0] === 'number' && typeof value[1] === 'number';
}

function projectRing(ring: unknown, project: Project): Point2D[] {
  if (!Array.isArray(ring)) return [];
  const points: Point2D[] = [];
  for (const position of ring) {
    if (!isPosition(position)) continue;
    const projected = project(position[0], position[1]);
    if (Number.isFinite(projected.x) && Number.isFinite(projected.y)) points.push(projected);
  }
  return points;
}

/* ──────────────────────────────── Lines ─────────────────────────────────── */

function lineLength(points: Point2D[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += Math.hypot(points[index].x - points[index - 1].x, points[index].y - points[index - 1].y);
  }
  return total;
}

/**
 * Point at a given distance along a polyline, plus the bearing there.
 * Used for the single mid-line label and for each repeat position.
 */
function pointAtDistance(points: Point2D[], distance: number): LabelAnchor | null {
  if (points.length < 2) return null;
  let travelled = 0;

  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const segment = Math.hypot(to.x - from.x, to.y - from.y);
    if (segment === 0) continue;

    if (travelled + segment >= distance) {
      const ratio = (distance - travelled) / segment;
      let angle = Math.atan2(to.y - from.y, to.x - from.x);
      // Keep text upright: a bearing pointing left would render upside down.
      if (angle > Math.PI / 2) angle -= Math.PI;
      if (angle < -Math.PI / 2) angle += Math.PI;
      return {
        x: from.x + (to.x - from.x) * ratio,
        y: from.y + (to.y - from.y) * ratio,
        angle,
      };
    }
    travelled += segment;
  }

  const last = points[points.length - 1];
  return { x: last.x, y: last.y, angle: 0 };
}

/** The visible drawing surface, in the same pixel space as a projected point. */
export interface ViewBounds {
  width: number;
  height: number;
  /** Slack around the edge so a label does not pop in right at the border. */
  margin?: number;
}

/**
 * Clips one segment to an axis-aligned rectangle (Liang–Barsky).
 * Returns the portion of [p0, p1] inside the rectangle, or null when the
 * whole segment misses it.
 */
function clipSegment(
  p0: Point2D,
  p1: Point2D,
  xMin: number,
  yMin: number,
  xMax: number,
  yMax: number,
): [Point2D, Point2D] | null {
  let t0 = 0;
  let t1 = 1;
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;

  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0; // parallel to this edge — only fails if outside it
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };

  if (!clip(-dx, p0.x - xMin)) return null;
  if (!clip(dx, xMax - p0.x)) return null;
  if (!clip(-dy, p0.y - yMin)) return null;
  if (!clip(dy, yMax - p0.y)) return null;

  return [
    { x: p0.x + t0 * dx, y: p0.y + t0 * dy },
    { x: p0.x + t1 * dx, y: p0.y + t1 * dy },
  ];
}

/**
 * Splits a polyline into the contiguous runs that fall within `bounds`.
 *
 * A label anchored to a feature's whole-geometry midpoint stays put even once
 * that midpoint has panned or zoomed off-screen — on a long pipeline, zooming
 * into any stretch other than its exact middle shows either nothing or a label
 * that has nothing to do with what's on screen. Clipping to the current
 * viewport first means the anchor tracks whatever part of the line is
 * actually visible, the same way a desktop GIS keeps a long road's label
 * centred on the visible stretch of it.
 */
function visibleChains(points: Point2D[], bounds: ViewBounds): Point2D[][] {
  const margin = bounds.margin ?? 64;
  const xMin = -margin;
  const yMin = -margin;
  const xMax = bounds.width + margin;
  const yMax = bounds.height + margin;

  const chains: Point2D[][] = [];
  let current: Point2D[] = [];

  for (let index = 1; index < points.length; index += 1) {
    const clipped = clipSegment(points[index - 1], points[index], xMin, yMin, xMax, yMax);
    if (!clipped) {
      if (current.length > 1) chains.push(current);
      current = [];
      continue;
    }
    const [start, end] = clipped;
    const last = current[current.length - 1];
    if (!last || Math.hypot(last.x - start.x, last.y - start.y) > 1e-6) {
      if (current.length > 1) chains.push(current);
      current = [start];
    }
    current.push(end);
  }
  if (current.length > 1) chains.push(current);
  return chains;
}

/**
 * Anchors along one polyline.
 * `repeat` of 0 places a single label at the midpoint; otherwise labels are
 * spaced every `repeat` pixels, which is what keeps a long pipeline readable
 * without labelling every vertex. When `bounds` is given, placement is
 * computed per visible run of the line rather than over its full length, so a
 * line with nothing on screen gets no anchor instead of a stale, off-screen one.
 */
function lineAnchors(points: Point2D[], repeat: number, bounds?: ViewBounds): LabelAnchor[] {
  const chains = bounds ? visibleChains(points, bounds) : [points];
  const anchors: LabelAnchor[] = [];

  for (const chain of chains) {
    const length = lineLength(chain);
    if (length === 0) continue;

    if (repeat <= 0) {
      const anchor = pointAtDistance(chain, length / 2);
      if (anchor) anchors.push(anchor);
      continue;
    }

    const before = anchors.length;
    // Start half a step in so the first label is not jammed against the end.
    for (let distance = repeat / 2; distance < length; distance += repeat) {
      const anchor = pointAtDistance(chain, distance);
      if (anchor) anchors.push(anchor);
      if (anchors.length >= 40) return anchors; // one feature must not flood the canvas
    }
    // This particular visible run is shorter than one repeat step — still
    // worth one label rather than none.
    if (anchors.length === before) {
      const anchor = pointAtDistance(chain, length / 2);
      if (anchor) anchors.push(anchor);
    }
  }

  return anchors;
}

/* ─────────────────────────────── Polygons ───────────────────────────────── */

/**
 * Signed area of a projected ring, by the shoelace formula.
 *
 * It must be the shoelace form specifically, not the equivalent trapezoid one:
 * the two differ in sign, and the centroid sum below divides by this area. A
 * mismatched sign negates the centroid, putting the anchor at -x,-y — off
 * screen, so the label silently never draws.
 */
function ringArea(points: Point2D[]): number {
  let sum = 0;
  for (let index = 0, previous = points.length - 1; index < points.length; previous = index, index += 1) {
    sum += points[previous].x * points[index].y - points[index].x * points[previous].y;
  }
  return sum / 2;
}

/**
 * Area-weighted centroid of the largest ring.
 *
 * Falls back to the bounding-box centre for degenerate rings (a sliver polygon
 * projected at low zoom can have zero area), which still gives a usable anchor
 * instead of NaN.
 */
function polygonAnchor(rings: Point2D[][]): LabelAnchor | null {
  let best: Point2D[] | null = null;
  let bestArea = -1;
  for (const ring of rings) {
    if (ring.length < 3) continue;
    const area = Math.abs(ringArea(ring));
    if (area > bestArea) {
      bestArea = area;
      best = ring;
    }
  }
  if (!best) return null;

  const area = ringArea(best);
  if (Math.abs(area) < 1e-6) {
    const xs = best.map((point) => point.x);
    const ys = best.map((point) => point.y);
    return {
      x: (Math.min(...xs) + Math.max(...xs)) / 2,
      y: (Math.min(...ys) + Math.max(...ys)) / 2,
      angle: 0,
    };
  }

  let x = 0;
  let y = 0;
  for (let index = 0, previous = best.length - 1; index < best.length; previous = index, index += 1) {
    const cross = best[previous].x * best[index].y - best[index].x * best[previous].y;
    x += (best[previous].x + best[index].x) * cross;
    y += (best[previous].y + best[index].y) * cross;
  }
  return { x: x / (6 * area), y: y / (6 * area), angle: 0 };
}

/* ──────────────────────────────── Entry ─────────────────────────────────── */

/**
 * Every anchor a geometry contributes.
 *
 * Multi-part geometries yield one anchor per part so a multipolygon district
 * made of several islands is labelled on each — matching what a desktop GIS
 * does — while `repeat` controls density along lines.
 */
export function anchorsForGeometry(
  geometry: GeoJSON.Geometry | null | undefined,
  project: Project,
  repeat = 0,
  bounds?: ViewBounds,
): LabelAnchor[] {
  if (!geometry) return [];

  switch (geometry.type) {
    case 'Point': {
      if (!isPosition(geometry.coordinates)) return [];
      const projected = project(geometry.coordinates[0], geometry.coordinates[1]);
      return Number.isFinite(projected.x) ? [{ ...projected, angle: 0 }] : [];
    }

    case 'MultiPoint':
      return projectRing(geometry.coordinates, project).map((point) => ({ ...point, angle: 0 }));

    case 'LineString':
      return lineAnchors(projectRing(geometry.coordinates, project), repeat, bounds);

    case 'MultiLineString':
      return (geometry.coordinates ?? []).flatMap((line) =>
        lineAnchors(projectRing(line, project), repeat, bounds),
      );

    case 'Polygon': {
      const anchor = polygonAnchor((geometry.coordinates ?? []).map((ring) => projectRing(ring, project)));
      return anchor ? [anchor] : [];
    }

    case 'MultiPolygon':
      return (geometry.coordinates ?? [])
        .map((polygon) => polygonAnchor(polygon.map((ring) => projectRing(ring, project))))
        .filter((anchor): anchor is LabelAnchor => anchor !== null);

    case 'GeometryCollection':
      return (geometry.geometries ?? []).flatMap((part) =>
        anchorsForGeometry(part, project, repeat, bounds),
      );

    default:
      return [];
  }
}
